import { FacetValue } from "./types";

/**
 * Reporting-period (cadence) definitions for the Batch Dashboard.
 *
 * Cadence is not a stored field — it is derived from the batch `configType`. France e-reporting
 * currently uses two cadences: 10-Day (SalesReport + PurchaseReport) and Monthly (PaymentReport).
 * Other countries may introduce new cadences later (e.g. Daily); add an entry here mapping the new
 * cadence to its configType(s) to surface it on the dashboard.
 *
 * `configTypes` are matched case-insensitively against the live `configType` facet values so the
 * actual stored casing is preserved when toggling the underlying `configTypeFilters`.
 */
export const REPORTING_PERIOD_DEFS: { value: string; configTypes: string[] }[] = [
    { value: "10-Day", configTypes: ["salesreport", "purchasereport"] },
    { value: "Monthly", configTypes: ["paymentreport"] },
    // { value: "Daily", configTypes: ["dailyreport"] },
];

/**
 * Resolves the concrete `configType` facet values (preserving their stored casing) that belong to a
 * given cadence. Returns an empty array for an unknown cadence or when none are present in the facet.
 */
export function resolveReportingPeriodConfigTypes(configTypeFacets: FacetValue[], cadence: string): string[] {
    const def = REPORTING_PERIOD_DEFS.find((d) => d.value === cadence);
    if (!def) {
        return [];
    }
    return configTypeFacets.filter((f) => def.configTypes.includes(f.value.toLowerCase())).map((f) => f.value);
}

/**
 * Builds the cadence facet (value + summed count) from the live `configType` facet. Cadences with no
 * matching documents are omitted so empty buckets are not rendered.
 */
export function buildReportingPeriodFacets(configTypeFacets: FacetValue[]): FacetValue[] {
    return REPORTING_PERIOD_DEFS.map((def) => ({
        value: def.value,
        count: configTypeFacets
            .filter((f) => def.configTypes.includes(f.value.toLowerCase()))
            .reduce((sum, f) => sum + f.count, 0),
    })).filter((f) => f.count > 0);
}

/**
 * Derives which cadences are active given the current `configTypeFilters`. An empty filter list means
 * "all" (the summary bar renders every cadence as selected), matching the behavior of the other facet
 * bars. Otherwise a cadence is active when ANY of its configTypes is filtered — so a filter that excludes
 * a cadence (e.g. selecting only SalesReport via the Batch Type bar) correctly dims the other cadences
 * instead of leaving them all highlighted.
 */
export function getSelectedReportingPeriods(configTypeFacets: FacetValue[], configTypeFilters: string[]): string[] {
    if (configTypeFilters.length === 0) {
        return [];
    }
    return REPORTING_PERIOD_DEFS.map((d) => d.value).filter((cadence) => {
        const group = resolveReportingPeriodConfigTypes(configTypeFacets, cadence);
        return group.length > 0 && group.some((t) => configTypeFilters.includes(t));
    });
}

/**
 * Computes the next `configTypeFilters` after toggling a cadence: if every configType in the cadence
 * group is already selected it removes them, otherwise it adds them (deduplicated). Returns the
 * previous list unchanged when the cadence has no matching configTypes.
 */
export function toggleReportingPeriodConfigTypes(configTypeFacets: FacetValue[], configTypeFilters: string[], cadence: string): string[] {
    const group = resolveReportingPeriodConfigTypes(configTypeFacets, cadence);
    if (group.length === 0) {
        return configTypeFilters;
    }
    const allSelected = group.every((t) => configTypeFilters.includes(t));
    return allSelected
        ? configTypeFilters.filter((t) => !group.includes(t))
        : Array.from(new Set([...configTypeFilters, ...group]));
}
